/**
 * In-app confirmation dialog — a drop-in replacement for window.confirm().
 *
 * WHY THIS EXISTS (owner bug report, 2026-09-23):
 * The SAIK iOS app renders the dashboard inside a WKWebView. In WKWebView a
 * native `window.confirm()` is a *no-op unless the host app implements*
 * `webView(_:runJavaScriptConfirmPanelWithMessage:initiatedByFrame:completionHandler:)`.
 * The SAIK app does not, so `confirm()` returned `false` immediately and every
 * `if (!confirm(...)) return;` bailed out before the request was ever sent —
 * the "Delete" trashcan looked dead (it only changed colour on hover) while the
 * backend received nothing at all.
 *
 * Any destructive action must therefore confirm **in the DOM**, not with the
 * browser/WebView chrome. `useConfirm()` returns a promise that resolves to the
 * chosen action value (`false` = cancelled), and works identically in Safari,
 * the iOS WKWebView app and every desktop browser.
 */
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import Button from './Button';

export interface ConfirmAction {
  label: string;
  /** Resolved value for this choice. `false` means "cancelled". */
  value: string | false;
  tone?: 'primary' | 'secondary' | 'danger';
}

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  /** Custom choices. Defaults to Cancel + Confirm. */
  actions?: ConfirmAction[];
}

/** Result is the chosen `value` (a string), or `false` when cancelled. */
export type ConfirmResult = string | false;

type ConfirmFn = (options: ConfirmOptions) => Promise<ConfirmResult>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Access the in-app confirm dialog. Must be inside <ConfirmProvider>. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fail loudly rather than silently falling back to the broken native confirm.
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}

/** Convenience wrapper: true when the user confirmed, false when they cancelled. */
export async function confirmBoolean(confirm: ConfirmFn, options: ConfirmOptions): Promise<boolean> {
  const result = await confirm({
    ...options,
    actions: options.actions ?? [
      { label: 'Cancel', value: false, tone: 'secondary' },
      { label: 'Confirm', value: 'confirm', tone: 'primary' },
    ],
  });
  return result !== false;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: ConfirmResult) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    // If a dialog is somehow already open, release the previous one first so no
    // caller is left hanging on an unresolved promise.
    resolver.current?.(false);
    return new Promise<ConfirmResult>((resolve) => {
      resolver.current = resolve;
      setState(options);
    });
  }, []);

  const settle = useCallback((value: ConfirmResult) => {
    const resolve = resolver.current;
    resolver.current = null;
    setState(null);
    resolve?.(value);
  }, []);

  // If the provider unmounts mid-dialog, resolve as cancelled so nothing hangs.
  useEffect(() => () => { resolver.current?.(false); resolver.current = null; }, []);

  const actions: ConfirmAction[] = state?.actions?.length
    ? state.actions
    : [
        { label: 'Cancel', value: false, tone: 'secondary' },
        { label: 'Confirm', value: 'confirm', tone: 'primary' },
      ];

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal open={!!state} onClose={() => settle(false)} title={state?.title} size="sm">
        <div data-agent-id="confirm.dialog" className="space-y-4">
          {state?.message && (
            <div className="text-sm text-slate-700 dark:text-slate-300">{state.message}</div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            {actions.map((action, index) => (
              <Button
                key={`${action.label}-${index}`}
                variant={action.tone ?? 'secondary'}
                onClick={() => settle(action.value)}
                data-agent-id={`confirm.action.${index}`}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
